import { Box, Stack } from '@mui/material';
import { useCallback } from 'react';
import { useWatch } from 'react-hook-form';

import { EditButton } from '@/components/form/baseInputs/Button/EditButton';
import { SelectInputOption } from '@/components/form/baseInputs/inputTypes';
import { FormAwareFormattedNumberInput } from '@/components/form/formAwareInputs/FormAwareFormattedNumberInput';
import { FormAwareSelectInput } from '@/components/form/formAwareInputs/FormAwareSelectInput';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { Card } from '@/components/layout/Card/Card';
import { useFormContext } from '@/components/react-hook-form';
import { getGreaterThanCurrentYearValidation } from '@/components/utils/inputUtils';
import {
  GrowthProfileEditorScope,
  growthProfileEditorTarget,
  GrowthProfileModal,
} from '@/modules/growthProfiles/GrowthProfileModal/GrowthProfileModal';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { useTrowser } from '@/modules/navigation/trowsers/trowser.context';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';

import {
  HypotheticalSaleLoanFormPaths,
  HypotheticalSaleLoanFormShape,
} from '../EstateWaterfallHypotheticalSaleLoanModal.types';
import { Operations } from './graphql/SaleLoanIllustration.generated';

interface SaleLoanIllustrationAssumptionsCardProps {
  growthProfileOptions: SelectInputOption<string>[];
  refetchProjections: () => void;
}

export function SaleLoanIllustrationAssumptionsCard({
  growthProfileOptions,
  refetchProjections,
}: SaleLoanIllustrationAssumptionsCardProps) {
  const { control } = useFormContext<HypotheticalSaleLoanFormShape>();
  const { householdId, isTwoClientHousehold } = useHouseholdDetailsContext();
  const [growthProfileId, firstGrantorDeathYear] = useWatch({
    control,
    name: [
      'growthProfileOverrideId',
      'firstGrantorDeathYear',
      'secondGrantorDeathYear',
    ],
  });
  // Edit and create are two targets on the same key, so which one the URL names
  // is also what tells the editor to create rather than update — no separate
  // "creating" flag.
  const growthProfileEditorTrowser = useTrowser(TrowserKey.GrowthProfile);
  const createTarget = growthProfileEditorTarget(
    GrowthProfileEditorScope.SaleLoanIllustration
  );
  const editTarget = growthProfileEditorTarget(
    GrowthProfileEditorScope.SaleLoanIllustration,
    growthProfileId
  );
  const isCreatingNewProfile =
    growthProfileEditorTrowser.target === createTarget;

  const validateCurrentYear = useCallback((v: unknown) => {
    return getGreaterThanCurrentYearValidation(v as number | null);
  }, []);

  const handleOpenGrowthProfileModal = useCallback(() => {
    growthProfileEditorTrowser.open({ target: editTarget });
  }, [editTarget, growthProfileEditorTrowser]);

  const handleCreateNewProfile = useCallback(() => {
    growthProfileEditorTrowser.open({ target: createTarget });
  }, [createTarget, growthProfileEditorTrowser]);

  const hasRequiredData = !!householdId;
  const isEditorOpen =
    isCreatingNewProfile || growthProfileEditorTrowser.target === editTarget;

  return (
    <>
      {hasRequiredData && isEditorOpen && (
        <GrowthProfileModal
          onAfterSave={() => refetchProjections()}
          onClose={growthProfileEditorTrowser.close}
          householdId={householdId}
          growthProfileId={isCreatingNewProfile ? undefined : growthProfileId}
          refetchQueries={[Operations.Query.GrowthProfileOptions]}
        />
      )}
      <Card variant="filled" sx={{ padding: 3 }}>
        {/* align items to the top of the row so that any errors are aligned correctly */}
        <Stack direction="row" spacing={4} alignItems="flex-start">
          <Stack direction="row" spacing={1} flexGrow={1} alignItems="flex-end">
            <Box flexGrow={1}>
              <FormAwareSelectInput
                control={control}
                required
                fieldName="growthProfileOverrideId"
                label="Growth profile"
                options={growthProfileOptions}
                showEmptyValue={false}
                addNewOption={{
                  text: 'Create new profile',
                  icon: <PlusIcon size={16} />,
                  onClick: handleCreateNewProfile,
                }}
              />
            </Box>
            <EditButton
              size="sm"
              aria-label="Edit growth profile"
              onClick={handleOpenGrowthProfileModal}
            />
          </Stack>
          <Stack direction="row" spacing={2} sx={{ flexShrink: 0 }}>
            <FormAwareFormattedNumberInput
              control={control}
              required
              fieldName={
                'firstGrantorDeathYear' as const satisfies HypotheticalSaleLoanFormPaths
              }
              label="Year of 1st death"
              decimalScale={0}
              formatHashString="####"
              fixedDecimalScale
              allowNegative={false}
              validateOnChange
              validation={{
                validate: validateCurrentYear,
              }}
            />
            {isTwoClientHousehold && (
              <FormAwareFormattedNumberInput
                control={control}
                required
                fieldName={
                  'secondGrantorDeathYear' as const satisfies HypotheticalSaleLoanFormPaths
                }
                label="Year of 2nd death"
                formatHashString="####"
                decimalScale={0}
                fixedDecimalScale
                allowNegative={false}
                validateOnChange
                validation={{
                  validateCurrentYear: (v) =>
                    getGreaterThanCurrentYearValidation(v as number | null),
                  validate: (value) => {
                    const valueNumber = value ? Number(value) : null;
                    if (
                      valueNumber &&
                      firstGrantorDeathYear &&
                      valueNumber < firstGrantorDeathYear
                    ) {
                      return `Must be ≥ ${firstGrantorDeathYear}`;
                    }

                    return undefined;
                  },
                }}
              />
            )}
          </Stack>
        </Stack>
      </Card>
    </>
  );
}
